import { FaceTracker, HeadMaskMesh } from '@zappar/zappar-react-three-fiber/lib/index-standalone';
import React, { ReactElement, FunctionComponent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { ISceneActions, ITransCatTypes, IUserActions, useDataStore, useSceneStore, useUserStore } from '../../../zustand';
import { useGetSubscribedPlacementModeRef } from '../../hoc/hocCustomHooks';
import { IFaceLandmark, IRoot, ISceneComp, ITrackingTypes } from '../../r3f-components/component-data-structure';
import FacePromptUI from './FacePromptUI/FacePromptUI';
import ScenesOriginContent from './ScenesOriginContent';
import { useCameraIsUserFacing } from '../../../hooks/general';
import { ITransitionPhase } from '../../r3f-components/transitions/spec';
import * as ZapparThree from "@zappar/zappar-threejs";
import { useFaceTriggers } from './faceTrackerHooks';
import { useThree } from '@react-three/fiber';

type IParentProps = {
	onShowContentFirstTime?: () => any;
	enabled: boolean;
	children: ReactElement[] | undefined;
	onMouthOpenChange?: (open: boolean) => unknown;
	onHeadTilt?: (side: 'left' | 'right') => unknown;
	onHeadShake?: () => unknown;
};

export const FaceTrackerWrapper: FunctionComponent<IParentProps> = ({ children, enabled, onShowContentFirstTime, onMouthOpenChange, onHeadTilt, onHeadShake }) => {
	const { scene } = useThree()
	const sceneDispatch = useSceneStore(({dispatch}) => dispatch);
	const userDispatch = useUserStore(({dispatch}) => dispatch);
	const activeSceneId = useSceneStore(({activeSceneId}) => activeSceneId);
	const firstSceneId = useDataStore(({componentsById, rootId}) => (componentsById?.[rootId as string] as IRoot)?.children[0]);
	const currentSceneId = activeSceneId || firstSceneId;
	const previousSceneId = useSceneStore(({previousSceneId}) => previousSceneId)
	const cameraIsUserFacing = useCameraIsUserFacing(1500);
	const isSnapshotUiVisible = useUserStore(({isSnapshotUiVisible}) => isSnapshotUiVisible);
	const isFaceTrackingVisible = useUserStore(({isFaceTrackingVisible}) => isFaceTrackingVisible);
	const faceTrackerGroupRef = useRef<ZapparThree.FaceAnchorGroup>(null);
	const [targetBeenVisibleSinceEnabled, setShowTargetBeenVisibleSinceEnabled] = useState(false);
	const placementModeRef = useGetSubscribedPlacementModeRef();
	const previousTransitionPhase = useSceneStore(({transitionPhase}) => transitionPhase[previousSceneId || '']);
	const componentsById = useDataStore(({componentsById}) => componentsById);
	const headMaskRef = useRef<any>(null!);

	const currentSceneIsFaceTracked = (componentsById?.[currentSceneId] as ISceneComp)?.trackingType === ITrackingTypes.face;
	const previousTransitionIsFinished = typeof previousTransitionPhase === 'undefined' ? true : previousTransitionPhase === ITransitionPhase.exitFinish;

	useFaceTriggers({
		enabled: enabled && isFaceTrackingVisible,
		onMouthOpenChange,
		onHeadShake,
		onHeadTilt,
		faceTrackerGroupRef,
		lipTopLandmark: scene.getObjectByName(IFaceLandmark.lipTop),
		lipBottomLandMark: scene.getObjectByName(IFaceLandmark.lipBottom)
	})

	useEffect(() => {
		if (!targetBeenVisibleSinceEnabled || placementModeRef.current || !isFaceTrackingVisible || !currentSceneIsFaceTracked ) return;
		sceneDispatch?.({
			type: ISceneActions.SET_TRANSITION_CATEGORY,
			transCatDict: { [currentSceneId]: ITransCatTypes.enter },
		});
	}, [targetBeenVisibleSinceEnabled, sceneDispatch, currentSceneId, isFaceTrackingVisible, currentSceneIsFaceTracked, placementModeRef]);

	const onVisible = useCallback(() => {
		if (enabled) {
			userDispatch({
				type: IUserActions.SET_FACE_TRACKING_VISIBLE,
				payload: { isFaceTrackingVisible: true },
			});
			setShowTargetBeenVisibleSinceEnabled(true);
			onShowContentFirstTime?.();
		}
	}, [onShowContentFirstTime, userDispatch, enabled]);

	const onNotVisible = useCallback(() => {
		userDispatch({
			type: IUserActions.SET_FACE_TRACKING_VISIBLE,
			payload: { isFaceTrackingVisible: false },
		});
	}, [userDispatch]);
	return (
		<>
			<FacePromptUI 
				isVisible={
					enabled && 
					!isFaceTrackingVisible && 
					!isSnapshotUiVisible &&
					cameraIsUserFacing &&
					previousTransitionIsFinished
				} 
			/>
			
			<FaceTracker ref={faceTrackerGroupRef} onVisible={onVisible} onNotVisible={onNotVisible} enabled={enabled} name="FaceTrackerWrapper" >
				<group visible={isFaceTrackingVisible}>
					<HeadMaskMesh ref={headMaskRef} trackerGroup={faceTrackerGroupRef} />
				</group>
				<ScenesOriginContent />
			</FaceTracker>
			<group visible={isFaceTrackingVisible}>{children}</group>
		</>
	);
};

FaceTrackerWrapper.displayName = 'FaceTrackerWrapper';
export default memo(FaceTrackerWrapper);
